import { css } from 'lit';
import { RenderResult } from '@typings/shared-types';
import { DirectiveResult } from 'lit/directive';
import { UnsafeHTMLDirective, unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DateOptions } from '@config/ConfigCARSx';
import { LOCALE } from '@/constants';
import formatTimezoneSup from './format-timezone-sup';

export enum SortDirection {
	ASC = 'ASC',
	DESC = 'DESC',
}

export interface TableColumn<T> {
	label: string;
	width?: string;
	minWidth?: string;
	columnFiltering?: boolean;
	columnSorting?: boolean;
	tooltip?: string;
	valueGetter(data: T): string | number | undefined | string[] | number[];
	titleRenderer?(data: T): string | number | undefined | string[] | number[];
	renderer?(data: T): RenderResult;
	// render extra content in cell that won't display in column filter dropdown
	extraRenderer?(data: T): RenderResult;
	isDate?: boolean;
	doNotPrint?: boolean;
	sticky?: boolean;

	onlyDisplayInCSV?: boolean;
}

export interface TableState<C extends string, F = undefined> {
	search: string;
	columnsExcluded: Record<C, string[]>;
	customFilter?: F;

	sortColumn?: C;
	sortDirection: SortDirection;
	visibleColumns: C[];

	pageRecordLimit: number;
	currentPage: number;
	totalRecordCount: number;

	selectedRowIds?: Array<string | number>;
}

export type TableColumns<C extends string, T> = Record<C, TableColumn<T>>;

export const tableRowIsNotExcludedByColumnFilters = <C extends string, T, F>(
	row: T,
	columns: TableColumns<C, T>,
	tableState?: TableState<C, F>,
): boolean => {
	return Object.entries(columns).every(([key, colRaw]) => {
		const col = colRaw as TableColumn<T>;
		const searchable = col.valueGetter(row);
		if (!searchable) {
			return true;
		}
		if (Array.isArray(searchable)) {
			const valueExcluded = tableState?.columnsExcluded?.[key as C]?.some((excluded) =>
				searchable.some((value) => String(value) === excluded),
			);
			return !valueExcluded;
		}
		const values = tableState?.columnsExcluded?.[key as C] || [];
		return !values.includes(String(searchable));
	});
};

export const FormatDate = new Intl.DateTimeFormat(LOCALE, DateOptions).format;

export const dateRenderer = (
	date?: number | string,
): DirectiveResult<typeof UnsafeHTMLDirective> | string => {
	if (!date) {
		return '--';
	}

	return unsafeHTML(formatTimezoneSup(FormatDate(new Date(date))));
};

export const dateTitleRenderer = (date?: number | string): string => {
	if (!date) {
		return '--';
	}
	return FormatDate(new Date(date));
};
